import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RootState} from '../../redux/store';
import styled, {css} from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Platform, View, TouchableOpacity} from 'react-native';
import {hideGlobalUploadModal} from './actions';
import {isWeb} from '../../utils/responsive';
import {
  cameraLaunch,
  galleryLaunch,
} from '../../utils/services/uploaderService';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {Theme} from '../../theme/lightTheme';
import {
  MediaFile
} from '../../interfaces/connected-components/uploadModal';
import {tr} from '../../translations';
import {uploadModalTypes} from '../../utils/enums';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import GenericButton from '../../components/button';
import Icon from '../../components/icon';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

// Web-specific modal styles
const WebOverlayContainer = styled.TouchableOpacity`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10003;
`;

const WebModalContainer = styled.View<{responsiveWidth: string}>`
  width: ${({responsiveWidth}) => responsiveWidth};
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 8px;
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 5px;
  shadow-opacity: 0.2;
  shadow-radius: 15px;
  elevation: 10;
  ${isWeb ? 'box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);' : ''}
  padding: 20px;
`;

const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
`;

const WebTitle = styled.Text`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  font-weight: bold;
`;

const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
  position: absolute;
  right: 0;
  top: -${({theme}) => theme.text.s6 / 2}px;
`;

// Mobile-specific styles remain unchanged
const Container = styled.View`
  width: 100%;
  height: 100%;
  overflow: hidden;
  align-self: center;
  align-items: center;
  justify-content: flex-end;
  z-index: 10003;
  position: absolute;
`;
const Overlay = styled(Animated.View)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`;
const ContentContainer = styled(Animated.View)<{
  bottomInset?: number;
  responsiveWidth: string;
}>`
  align-items: center;
  align-self: center;
  overflow: hidden;
  width: ${({responsiveWidth}) => responsiveWidth};
  z-index: 10003;
  border-top-left-radius: 25px;
  border-top-right-radius: 25px;
  background-color: ${({theme}) => theme.screenBackground};
  padding-top: ${({theme}) => theme.text.s55}px;
  padding-bottom: ${({bottomInset, theme}) =>
    Math.max((bottomInset || 0) + theme.text.s12, theme.text.s55)}px;

  ${Platform.OS === 'ios'
    ? css`
        shadowcolor: ${({theme}) => theme.text.dark};
        shadowopacity: 0.2;
        shadowradius: 4px;
        shadowoffset: {
          height: 2px;
        }
      `
    : ''}

  ${Platform.OS === 'android'
    ? css`
        elevation: 5;
      `
    : ''}
  
  ${Platform.OS === 'web'
    ? css`
        box-shadow: 0px 2px 4px ${({theme}) => `${theme.text.dark}33`};
      `
    : ''}
`;

const GlobalUploadModal = ({uploadModal}: any) => {
  const theme = useTheme() as Theme;
  const {isVisible, onSelectionChange, options} = uploadModal || {};
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {hdp, isMobile, getByScreenSize} = useResponsiveDimensions();
  const screenHeight = hdp(100);

  const [modalVisible, setModalVisible] = useState(false);
  const overlayOpacity = useSharedValue(0);
  const scale = useSharedValue(0);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  useEffect(() => {
    overlayOpacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    scale.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    opacity.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    translateY.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    if (isVisible) {
      setModalVisible(true);
    } else {
      setTimeout(() => setModalVisible(false), 300);
    }
  }, [isVisible]);

  const choicesList = [
    {
      label: 'gallery',
      onPress: () => uploadFile(uploadModalTypes.gallery),
    },
    {
      label: 'camera',
      onPress: () => uploadFile(uploadModalTypes.camera),
    },
    {
      label: 'cancel',
      onPress: () => hideGlobalUploadModal(),
    },
  ];

  const renderButton = (item: any, index: number) => (
    <GenericButton
      key={index}
      label={tr(`imagePicker.${item.label}`)}
      labelStyle={{
        color:
          item.label === 'cancel'
            ? theme.text.light
            : theme.questionModal.title,
      }}
      onPress={() => {
        item.onPress && item?.onPress();
        hideGlobalUploadModal();
      }}
      outline={item.label !== 'cancel'}
      radius={100}
      style={{
        width: '90%',
        alignSelf: 'center',
        borderWidth: 1,
        borderColor: theme.primary + '22',
        marginBottom: '2%',
        backgroundColor:
          item.label === 'cancel' ? theme.text.error : 'transparent',
      }}
    />
  );

  const uploadFile = async (type: uploadModalTypes) => {
    hideGlobalUploadModal();
    let file: MediaFile | undefined;
    if (type === uploadModalTypes.camera) {
      file = await cameraLaunch(options, {
        title: tr(`permissions.${String(type)}GalleryTitle`),
        message: tr(`permissions.${String(type)}GalleryMessage`),
        buttonPositive: tr('permissions.galleryButtonPositive'),
        buttonNegative: tr('permissions.galleryButtonNegative'),
      });
    } else {
      file = await galleryLaunch(options, {
        title: tr(`permissions.${String(type)}CameraTitle`),
        message: tr(`permissions.${String(type)}CameraMessage`),
        buttonPositive: tr('permissions.cameraButtonPositive'),
        buttonNegative: tr('permissions.cameraButtonNegative'),
      });
    }
    if (!file) {
      return;
    }
    if (onSelectionChange) {
      onSelectionChange(file);
    }
    hideGlobalUploadModal();
  };

  const overlayStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: `rgba(0, 0, 0, ${overlayOpacity.value * 0.2})`,
    };
  });
  const translateStyle = useAnimatedStyle(() => {
    const translate = interpolate(translateY.value, [0, 1], [screenHeight, 0]);
    return {
      transform: [{translateY: translate}],
    };
  });

  // Web-specific render
  if (isWeb && !isMobile && modalVisible) {
    return (
      <WebOverlayContainer activeOpacity={1} onPress={hideGlobalUploadModal}>
        <WebModalContainer responsiveWidth={getByScreenSize('90%', '400px')}>
          <WebHeaderRow>
            <WebTitle>{tr('imagePicker.title')}</WebTitle>
            <WebCloseButton onPress={hideGlobalUploadModal}>
              <Icon
                name={'close'}
                size={theme.text.s6}
                color={theme.text.dark}
              />
            </WebCloseButton>
          </WebHeaderRow>
          <View style={{alignItems: 'center'}}>
            {choicesList.map(renderButton)}
          </View>
        </WebModalContainer>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={hideGlobalUploadModal}
          activeOpacity={1}
        />
      </WebOverlayContainer>
    );
  }

  // Mobile render remains unchanged
  if (!modalVisible) {
    return null;
  }

  return (
    <Container>
      <Overlay
        style={overlayStyle}
        onStartShouldSetResponder={() => true}
        onResponderGrant={hideGlobalUploadModal}
      />
      <ContentContainer
        responsiveWidth={getByScreenSize(100, 40) + '%'}
        bottomInset={bottomInset}
        style={translateStyle}>
        {choicesList.map(renderButton)}
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    uploadModal: state.uploadModal,
  };
};

export default connect(mapStateToProps)(GlobalUploadModal);
